import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Box, Button, Fade, InputAdornment, OutlinedInput, Stack, Typography } from '@mui/material';
import { MetrcLogo } from '../../utility';
import DisplayMetrcTagsTable from '../../utility/tables/DisplayMetrcTagsTable';
import SearchIcon from '@mui/icons-material/Search';
import { roomDetailClasses, tableClasses, errFeedbackFn } from '../../utility';
import apiService from '../../../api/apiService';
import { FeedbackContext } from '../../../App';
import { useToken } from '../../../utility';
import { failsTokenAndSetupCheck } from '../../../checks/setup';
import EntityDetailPopup from '../../utility/EntityDetailPopup';
import LabTestExpandCard from '../../utility/popups/LabTestExpandCard';

export default function _LabTestBatchesTab(props) {
  const {
    currDarkThemeClass = '',
    ...otherProps
  } = props;

  const [syncLoading, setSyncLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Popup state
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupBatchName, setPopupBatchName] = useState('');
  const [popupTestTypes, setPopupTestTypes] = useState([]);
  const [popupItemCategoriesOpen, setPopupItemCategoriesOpen] = useState(false);
  const [popupBatchItemCategories, setPopupBatchItemCategories] = useState([]);
  const [popupBatchItemCategoryName, setPopupBatchItemCategoryName] = useState('');

  const feedbackCX = useContext(FeedbackContext);
  const { token, tokenDetails } = useToken();
  const invalidTokenState = useMemo(() => failsTokenAndSetupCheck(token, tokenDetails), [token, tokenDetails]);

  // Debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); // Reset page when search changes
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchBatchData = async (currentPage = page, limit = rowsPerPage, search = debouncedSearchTerm) => {
    setLoading(true);
    try {
      let url = `labtest-batches/?limit=${limit}&page=${currentPage + 1}`;
      if (search) {
        url += `&name__icontains=${encodeURIComponent(search)}`;
      }
      const response = await apiService.fetchInstance(url);
      if (response?.data) {
        const formattedData = formatData(response.data.results || []);
        setData(formattedData);
        setTotalCount(response.data.count || 0);
      }
    } catch (error) {
      feedbackCX.setContextValue(true, errFeedbackFn(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToMetrc = async () => {
    setSyncLoading(true);
    try {
      const batchResponse = await apiService.fetchInstance('labtest-batches/?sync_to_metrc=true');
      const typeResponse = await apiService.fetchInstance('labtest-types/?sync_to_metrc=true');
      if (batchResponse.status === 200 && typeResponse.status === 200) {
        fetchBatchData();
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Lab Test Batches & Types Synced From Metrc Successfully.',
        });
        setTimeout(() => {
          feedbackCX.setContextValue(false, {});
        }, 5000);
      }
    } catch (error) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: `Error syncing lab test batches/types from Metrc`,
      });
    } finally {
      setSyncLoading(false);
    }
  };

  useEffect(() => {
    if (invalidTokenState) return;
    fetchBatchData(page, rowsPerPage, debouncedSearchTerm);
    // eslint-disable-next-line
  }, [page, rowsPerPage, debouncedSearchTerm, invalidTokenState]);

  
  const handleTestTypeCountClick = (batch) => {
    setPopupBatchName(batch.name);
    setPopupOpen(true);
    setPopupTestTypes(batch.test_types || []);
  };

  const handleItemCategoryCountClick = (batch) => {
    setPopupBatchItemCategoryName(batch.name);
    setPopupItemCategoriesOpen(true);
    setPopupBatchItemCategories(batch.item_categories || []);
  };

  const renderTestTypeExpandCards = () => {
    if (!popupTestTypes.length) {
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 2 }}>
          No test types found for this batch.
        </Typography>
      );
    }
    return (
      <Stack spacing={1}>
        {popupTestTypes.map((typeObj, idx) => {
          const type = typeObj.lab_test_type || {};
          const testTypeName =
            type.name ||
            typeObj.name ||
            `Test Type #${idx + 1}`;

          return (
            <LabTestExpandCard
              key={type.id || idx}
              title={testTypeName}
              currDarkThemeClass={currDarkThemeClass}
              entries={[
                { key: 'Requires Test Result', value: String(type.requires_test_result) },
                { key: 'Informational Only', value: String(type.informational_only) },
                { key: 'Research & Development', value: String(type.research_and_development) },
                { key: 'Always Passes', value: String(type.always_passes) },
                { key: 'Max Allowed Failure Count', value: String(type.max_allowed_failure_count) },
                { key: 'Lab Test Result Mode', value: String(type.lab_test_result_mode) },
                { key: 'Lab Test Result Minimum', value: String(type.lab_test_result_minimum) },
                { key: 'Lab Test Result Maximum', value: String(type.lab_test_result_maximum) },
                { key: 'Dependency Mode', value: String(type.dependency_mode) },
              ]}
            />
          );
        })}
      </Stack>
    );
  };

  const renderItemCategoryExpandCards = () => {
    if (!popupBatchItemCategories.length) {
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 2 }}>
          No item categories found for this batch.
        </Typography>
      );
    }
    return (
      <Stack spacing={1}>
        {popupBatchItemCategories.map((catObj, idx) => {
          const cat = catObj.item_category || {};
          const categoryName =
            cat.product_category_name ||
            cat.product_category_id ||
            `Category #${idx + 1}`;

          return (
            <LabTestExpandCard
              key={cat.product_category_id || idx}
              title={categoryName}
              currDarkThemeClass={currDarkThemeClass}
              entries={[
                { key: 'Product Category ID', value: String(cat.product_category_id) },
                { key: 'Product Category Name', value: String(cat.product_category_name) },
              ]}
            />
          );
        })}
      </Stack>
    );
  };

  
  const formatData = formattedData => {
    return formattedData.map((batch, idx) => [
      `${idx + 1 + page * rowsPerPage}`.padStart(2, '0'),
      batch.name || '',
      batch.lab_test_type_count
        ? (
          <Button
            variant="text"
            sx={{
              color: isDark ? '#B1D23A' : '#222', 
              cursor: 'pointer',
              minWidth: 0,
              padding: 0,
              display: 'block',
              textAlign: 'center',
              textDecoration: 'underline',
              width: '100%',
            }}
            onClick={() => handleTestTypeCountClick(batch)}
          >
            {batch.lab_test_type_count}
          </Button>
        )
        : (
          <Typography
            sx={{
              color: isDark ? '#B1D23A' : '#222',
              minWidth: 0,
              padding: 0,
              textAlign: 'center',
              display: 'block',
              width: '100%',
            }}
          >
            0
          </Typography>
        ),
      batch.item_category_count
        ? (
          <Button
            variant="text"
            sx={{
              color: isDark ? '#B1D23A' : '#222',
              cursor: 'pointer',
              minWidth: 0,
              padding: 0,
              display: 'block',
              textAlign: 'center',
              textDecoration: 'underline',
              width: '100%',
            }}
            onClick={() => handleItemCategoryCountClick(batch)}
          >
            {batch.item_category_count}
          </Button>
        )
        : (
          <Typography
            sx={{
              color: isDark ? '#B1D23A' : '#222',
              minWidth: 0,
              padding: 0,
              textAlign: 'center',
              display: 'block',
              width: '100%',
            }}
          >
            0
          </Typography>
        ),
      batch.required ? 'Yes' : 'No',
    ]);
  };

  const batchKeyTitleMap = [
    { key: 'name', title: 'Name' },
    { key: 'lab_test_type_count', title: 'Lab Test Type Count' },
    { key: 'item_category_count', title: 'Item Category Count' },
    { key: 'required', title: 'Required' },
  ];

  const tableTitles = batchKeyTitleMap.map(e => ({ title: e.title }));

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  // Simplified handleSearch
  const handleSearch = event => {
    setSearchTerm(event.target.value);
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const outlinedInputProps = {
    className: `${roomDetailClasses.filterInput} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search using Batch Name',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    ),
    onChange: handleSearch,
    onKeyPress: handleKeyPress,
    value: searchTerm,
  };

  const isDark = currDarkThemeClass && currDarkThemeClass.includes('dark');

  const batchTableValues = {
    animationClass: 'farmsTabContent',
    currDarkThemeClass,
    emptyTableMsg: 'No Lab Test Batches Found (Please click on Sync From Metrc)',
    entity: 'Lab Test Batches',
    tableEntries: data,
    tableTitles: [{ title: 'Sl. No' }, ...tableTitles],
    toShowAddBtn: false,
    page,
    rowsPerPage,
    totalCount,
    onPageChange: handlePageChange,
    loading,
    ...otherProps,
  };

  return (
    <Fade in={true} timeout={2000}>
      <Box>
        <Stack direction="row" justifyContent="flex-end" padding={2}>
          <Button
            onClick={handleSyncToMetrc}
            disabled={syncLoading}
            style={{ display: 'flex', alignItems: 'center', paddingRight: '20px' }}
            sx={{ color: '#B1D23A', '&.Mui-disabled': { color: '#B1D23A' } }}
          >
            <MetrcLogo style={{ width: 24, height: 24, marginRight: 8 }} />
            {syncLoading ? 'Syncing...' : 'Sync From Metrc'}
          </Button>
          <OutlinedInput {...outlinedInputProps} />
        </Stack>
        <Box className={`${tableClasses?.container} ${currDarkThemeClass}`}>
          <DisplayMetrcTagsTable {...batchTableValues} />
        </Box>
        <EntityDetailPopup
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          currDarkThemeClass={currDarkThemeClass}
          toShowSave={false}
          toShowCreate={false}
          headContentText=""
          title={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                pr: 2,
              }}
            >
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: "8px",
                  px: 2,
                  py: 0.7,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600, 
                    color: 'text.primary',
                    display: "inline",
                  }}
                >
                  Test Types for Batch:
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 400, 
                    color: 'text.primary',
                    display: "inline",
                    ml: 1,
                  }}
                >
                  {popupBatchName}
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: "8px",
                  px: 1.5,
                  py: 0.2,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  ml: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600, 
                    color: 'text.primary',
                    display: "inline",
                  }}
                >
                  Count:
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 400, 
                    color: 'text.primary',
                    display: "inline",
                    ml: 1,
                  }}
                >
                  {popupTestTypes?.length || 0}
                </Typography>
              </Box>
            </Box>
          }
          otherEntityModalPaperProps={{
            sx: {
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              borderRadius: 0,
              bgcolor: "black",
              p: 3,
              boxShadow: "none",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              mt: 2,
              pr: 1,
            }}
          >
            <Stack spacing={1} sx={{ width: "100%" }}>
              {renderTestTypeExpandCards()}
            </Stack>
          </Box>
        </EntityDetailPopup>
        <EntityDetailPopup
          open={popupItemCategoriesOpen}
          onClose={() => setPopupItemCategoriesOpen(false)}
          currDarkThemeClass={currDarkThemeClass}
          toShowSave={false}
          toShowCreate={false}
          headContentText=""
          title={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                pr: 2,
              }}
            >
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: "8px",
                  px: 2,
                  py: 0.7,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    display: "inline",
                  }}
                >
                  Item Categories for Batch:
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 400,
                    color: 'text.primary',
                    display: "inline",
                    ml: 1,
                  }}
                >
                  {popupBatchItemCategoryName}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    display: "inline",
                  }}
                >
                  Count:
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 400,
                    color: 'text.primary',
                    display: "inline",
                    ml: 1,
                  }}
                >
                  {popupBatchItemCategories?.length || 0}
                </Typography>
              </Box>
            </Box>
          }
          otherEntityModalPaperProps={{
            sx: {
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              borderRadius: 0,
              bgcolor: "black",
              p: 3,
              boxShadow: "none",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              mt: 2,
              pr: 1,
            }}
          >
            <Stack spacing={1} sx={{ width: "100%" }}>
              {renderItemCategoryExpandCards()}
            </Stack>
          </Box>
        </EntityDetailPopup>
      </Box>
    </Fade>
  );
}